import { Model } from 'dva'
import { message, notification } from 'antd'
import { FlowConfig } from './types'
import { OrganisationsService } from '../../Service'
import { IsObject } from '../../Utils/helpers'
import { FailureHandler } from 'Utils/ApiHelper'

const newFlowConfig: FlowConfig = {
  id: null,
  version: '',
  slu_model: null,
  conditional_slu_model: [],
  asr_provider: null,
  condional_asrs: [],
  bi_enable: false,
  ld_enable: false,
  ld_consecutive_turns: 0,
  ld_limit: 0,
  ld_languages: [],
  su_language: null,
  su_threshold: 0,
  su_shadow_mode_enable: false,
  language: null,
  conditional_languages: [],
  base_url: '',
  audio_overlay_url: '',
  max_retry_attempts: 0,
  state_max_retry_attempts: 0,
  allow_callback: false,
  log_request: false,
  default_intent: null,
  metadata_key_history: '',
  // events: ,
  mask_enable: false,
  mask_with: '',
  mask_type: '',
  output_format: '',
  audio_format: '',
  max_transit_limit: 0,
  ab_test: [],
}

const FlowConfigs: Model = {
  namespace: 'flowConfigs',
  state: {
    list: [],
    loading: true,
    bargeInModalLoading: false,
    showSettingsModal: false,
    search: '',
    flowConfig: newFlowConfig,
    blocks: [],
    editFlowConfig: newFlowConfig,
    editFlowConfigLoading: false,
    editCurrentTab: 'general',
    loadingYaml: false,
    updateBargeInBlocks: {},
    bargeInGlobalBehaviour: null,
    yaml_values: {
      states: '',
      intents: '',
      actions: '',
      triggers: '',
      nls_labels: '',
    },
    searchModal: {
      isVisible: false,
    },
  },
  effects: {
    *list(
      { useCache = true, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      try {
        let flowConfigs = yield select((state: any) => state.flowConfigs.list)
        if (!(flowConfigs.length > 0 && useCache)) {
          flowConfigs = yield call(OrganisationsService.organisationsFlowsVersionsList, {
            organisationUuid,
            flowUuid: flowUuid,
          })
          yield put({ type: 'modifyList', payload: flowConfigs })
        }
        onSuccess(flowConfigs)
      } catch (err: any) {
        FailureHandler('Error fetching versions', err, onFailure)
      }
      setLoading(false)
    },
    *retrieve(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, useCache = true },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const version = yield select((state: any) => state.flowLayout.version?.version)
      try {
        let flowConfig
        if (useCache) {
          flowConfig = yield select((state: any) =>
            state.flowConfigs.list.find((fc: FlowConfig) => fc.version === version)
          )
        }
        if (!flowConfig && version) {
          flowConfig = yield call(OrganisationsService.organisationsFlowsVersionsRetrieve, {
            organisationUuid,
            flowUuid: flowUuid,
            version: version,
          })
          yield put({
            type: 'setFlowConfig',
            payload: flowConfig,
          })
        }
        yield put({ type: 'modifyList', payload: flowConfig })
        onSuccess(flowConfig)
      } catch (err) {
        FailureHandler('Could not find the version!', err, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      { data, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const version = yield select((state: any) => state.flowLayout.version?.version)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPartialUpdate, {
          organisationUuid,
          flowUuid: flowUuid,
          version: version,
          requestBody: data,
        })
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error updating version', err, onFailure)
      }
      setLoading(false)
    },
    *fetchFlowConfig({ setEditFlowConfig }, { call, put, select }): any {
      yield put({ type: 'toggleLoading', payload: true })
      // const response = yield call(getFlowConfig);
      // TODO: Confirm with mukunth on what happens here?
      const flowLayout = yield select((state: any) => state.flowLayout)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsRetrieve, {
          organisationUuid: flowLayout.flow?.organisation?.uuid,
          flowUuid: flowLayout.flow.uuid,
          version: flowLayout.version.version,
        })
        yield put({
          type: 'setFlowConfig',
          payload: response,
        })
        if (setEditFlowConfig) {
          yield put({
            type: 'setEditFlowConfig',
            payload: response,
          })
        }
      } catch (e: any) {
        message.error('Could not fetch the flow settings')
      }
      yield put({ type: 'toggleLoading', payload: false })
    },
    *editFlowConfig({ payload }, { call, put, select }): any {
      yield put({ type: 'toggleEditFlowConfigLoading', payload: true })
      try {
        const organisationUuid = yield select(
          (state: any) => state.flowLayout.flow?.organisation?.uuid
        )
        yield call(OrganisationsService.organisationsFlowsVersionsPartialUpdate, {
          organisationUuid,
          requestBody: payload.data,
          id: [payload.id],
        })
        message.success(`Updated`)
      } catch (e) {
        message.error(`Could not update`)
      }
      yield put({ type: 'fetchFlowConfig', setEditFlowConfig: true })
      yield put({ type: 'toggleEditFlowConfigLoading', payload: false })
    },
    *fetchBlocksWithBargeIn({}, { call, put, select }): any {
      yield put({ type: 'toggleShowSettingsModal', payload: true })
      yield put({ type: 'toggleBargeInModalLoading', payload: true })
      const flowLayout = yield select((state: any) => state.flowLayout)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsBlocksList, {
          organisationUuid: flowLayout.flow?.organisation?.uuid,
          flowUuid: flowLayout.flow.uuid,
          flowConfigVersion: flowLayout.version.version,
          type: ['Voice'],
          fields: ['id', 'name'],
        })
        yield put({ type: 'setBlocksData', payload: response })
      } catch (e) {
        message.error('Error Retrieving Details')
      }
      yield put({ type: 'toggleBargeInModalLoading', payload: false })
    },
    *updateBargeInBlocksData({ call, put, select }): any {
      yield put({ type: 'toggleShowSettingsModal', payload: true })
      const flowLayout = yield select((state: any) => state.flowLayout)
      // Update barge-in block data
      const blocksToBeUpdated = yield select((state: any) => state.flowConfigs.updateBargeInBlocks)
      let updateSuccess = true
      for (const id in blocksToBeUpdated) {
        try {
          yield call(OrganisationsService.organisationsFlowsVersionsBlocksPartialUpdate, {
            organisationUuid: flowLayout.flow?.organisation?.uuid,
            flowUuid: flowLayout.flow.uuid,
            flowConfigVersion: flowLayout.version.version,
            id,
            requestBody: blocksToBeUpdated[id],
          })
        } catch (e) {
          updateSuccess = false
          break
        }
      }
      if (updateSuccess) {
        message.success('Successfully Updated!')
        const response = yield call(OrganisationsService.organisationsFlowsVersionsBlocksList, {
          organisationUuid: flowLayout.flow.organisation.uuid,
          flowUuid: flowLayout.flow.uuid,
          flowConfigVersion: flowLayout.version.version,
          type: ['Voice'],
        })
        yield put({ type: 'setBlocksData', payload: response })
        yield put({ type: 'resetBargeInUpdations', payload: {} })
      } else {
        message.error('Updation Failed!!')
      }
      yield put({ type: 'toggleBargeInModalLoading', payload: false })
    },
    *updateGlobalBargeInBehaviour({ payload }, { call, put, select }): any {
      yield put({ type: 'toggleBargeInModalLoading', payload: true })
      const flowLayout = yield select((state: any) => state.flowLayout)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPartialUpdate, {
          organisationUuid: flowLayout.flow?.organisation?.uuid,
          flowUuid: flowLayout.flow.uuid,
          version: flowLayout.version.version,
          requestBody: payload.data,
          id: payload.id,
        })
        yield put({ type: 'setBargeInGlobalBehaviour', payload: response.bi_behaviour })
        message.success(`Updated`)
      } catch (e) {
        message.error(`Could not update`)
      }
      yield put({ type: 'toggleBargeInModalLoading', payload: false })
    },
    *create(
      { data, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const flowLayout = yield select((state: any) => state.flowLayout)
      try {
        const version = yield call(OrganisationsService.organisationsFlowsVersionsCreate, {
          organisationUuid: flowLayout.flow?.organisation?.uuid,
          flowUuid: flowLayout.flow.uuid,
          requestBody: data,
        })
        yield put({
          type: 'flowLayout/addVersionToSelectedFlow',
          payload: version.version,
        })
        onSuccess(version)
      } catch (e: any) {
        FailureHandler('Could not create version', e, onFailure)
      }
      setLoading(false)
    },
  },
  reducers: {
    toggleLoading(state, action) {
      return {
        ...state,
        loading: action.payload,
      }
    },
    toggleBargeInModalLoading(state, action) {
      return {
        ...state,
        bargeInModalLoading: action.payload,
      }
    },
    toggleEditFlowConfigLoading(state, action) {
      return {
        ...state,
        editFlowConfigLoading: action.payload,
      }
    },
    setFlowConfig(state, action) {
      return {
        ...state,
        flowConfig: action.payload,
      }
    },

    setEditFlowConfig(state, action) {
      return {
        ...state,
        editFlowConfig: {
          ...state.flowConfig,
          ...action.payload,
        },
      }
    },
    setEditCurrentTab(state, action) {
      return {
        ...state,
        editCurrentTab: action.payload,
      }
    },
    setSearch(state, action) {
      return {
        ...state,
        search: action.payload,
      }
    },
    toggleLoadingYaml(state, action) {
      return {
        ...state,
        loadingYaml: action.payload,
      }
    },
    saveYaml(state, action) {
      return {
        ...state,
        yaml_values: action.payload,
      }
    },
    setBlocksData(state, action) {
      return {
        ...state,
        blocks: action.payload,
      }
    },
    toggleShowSettingsModal(state, action) {
      return {
        ...state,
        showSettingsModal: action.payload,
      }
    },
    addUpdationsToBarginBlocks(state, action) {
      const id = action.id
      const updateBlocks = state.updateBargeInBlocks
      if (action.id in updateBlocks) {
        updateBlocks[id] = {
          ...updateBlocks[id],
          ...action.payload,
        }
      } else {
        updateBlocks[id] = {
          ...action.payload,
        }
      }
      return { ...state, ...updateBlocks }
    },
    resetBargeInUpdations(state) {
      return {
        ...state,
        updateBargeInBlocks: {},
      }
    },
    setBargeInGlobalBehaviour(state, action) {
      return {
        ...state,
        bargeInGlobalBehaviour: action.payload,
      }
    },
    modifyList(state, action) {
      // clone the list
      let list = [...state.list]
      if (IsObject(action.payload)) {
        // if the payload is an object, it's a single object, so add it to the list
        list = list.filter((l: FlowConfig) => l.id !== action.payload.id)
        list.push(action.payload)
      } else if (Array.isArray(action.payload)) {
        // if the payload is an array, it's a list of objects, so add them to the list
        list = list
          .filter((l: FlowConfig) => !action.payload.map((p: FlowConfig) => p.id).includes(l.id))
          .concat(action.payload)
      } else if (Number.isInteger(action.payload)) {
        // if the payload is an integer, it's an id sent in delete operations, so remove it from the list
        list = list.filter((l: FlowConfig) => l.id !== action.payload)
      }
      return {
        ...state,
        list: list,
      }
    },
    toggleSearchModal(state, action) {
      const { visible } = action.payload
      if (visible) {
        return {
          ...state,
          searchModal: {
            ...state.searchModal,
            isVisible: true,
          },
        }
      }
      return {
        ...state,
        searchModal: {
          ...state.searchModal,
          isVisible: false,
        },
      }
    },
    resetFlowConfigs(state, action) {
      return {
        ...state,
        list: [],
        loading: true,
        bargeInModalLoading: false,
        showSettingsModal: false,
        search: '',
        flowConfig: newFlowConfig,
        blocks: [],
        editFlowConfig: newFlowConfig,
        editFlowConfigLoading: false,
        editCurrentTab: 'general',
        loadingYaml: false,
        updateBargeInBlocks: {},
        bargeInGlobalBehaviour: null,
        yaml_values: {
          states: '',
          intents: '',
          actions: '',
          triggers: '',
          nls_labels: '',
        },
        searchModal: {
          isVisible: false,
        },
      }
    },
  },
}

export default FlowConfigs
